import React from "react";
import { object, number } from "prop-types";
import { useSelector } from "react-redux";
import { collectionToStories, Link } from "@quintype/components";
import get from "lodash/get";
import { ResearchCard } from "../../atoms/research-card";
import { getSlug } from "../../../arrow/utils/utils";
import { filterStoriesByForum } from "../../../utils/forum-filter";
import { filterStoriesByTemplate } from "../../../utils/story-utils";
import "./research-hub-row.m.css";

/**
 * ResearchHubRow component displays a collection row in the Research Hub
 * Features:
 * - Collection name as section title
 * - Three column grid layout (responsive)
 * - Optional custom background color for cards in the first collection
 * - Displays story cards using ResearchCard component
 */
export const ResearchHubRow = ({ collection, index }) => {
  const config = useSelector((state) => get(state, ["qt", "config"], {}));
  const member = useSelector((state) => get(state, ["member"], null));
  const subscriberMetadata = useSelector((state) => get(state, ["subscriberMetadata"], null));

  // Get all stories from collection
  const allStories = collectionToStories(collection);

  // Filter out stories with specific story templates
  const templateFilteredStories = filterStoriesByTemplate(allStories);

  // Apply forum-based filtering to the template-filtered stories
  const stories = filterStoriesByForum(
    templateFilteredStories.map(story => ({ type: "story", story })),
    member,
    subscriberMetadata
  ).map(wrapper => wrapper.story);

  if (!stories || stories.length === 0) {
    return null;
  }

  // Get custom background color from collection metadata for the first collection (if specified in Bold CMS)
  const cardBackgroundColor = get(collection, ['associated-metadata', 'card_background_color'], null);
  const maxNoOfCards = get(collection, ['associated-metadata', 'number_of_cards'], stories.length);

  // Get collection URL using the utility function which handles both collections and sections
  const collectionUrl = getSlug(collection, config);

  // Limit the number of stories to render based on maxNoOfCards from CMS
  const storiesToRender = stories.slice(0, maxNoOfCards);

  return (
    <section styleName="research-hub-row">
      <div styleName="row-container">
        {collection.name && (
          <>
            <div styleName="research-hub-row-title-link">
              <Link href={collectionUrl}>
                <h2 styleName="research-hub-row-title">{collection.name}</h2>
              </Link>
            </div>
            <div styleName="research-hub-row-title-border"></div>
          </>
        )}

        <div styleName="research-hub-row-grid">
          {storiesToRender.map((story, storyIndex) => (
            <div key={`${story.id}-${storyIndex}`} styleName="research-hub-row-item">
              <ResearchCard
                story={story}
                position={storyIndex}
                backgroundColor={cardBackgroundColor}
                showBottomBorder={!cardBackgroundColor}
                hideImage={index > 0}
              />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

ResearchHubRow.propTypes = {
  collection: object,
  index: number,
};

ResearchHubRow.storyLimit = 12;
